import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { get, post } from '../../services/apiClient';
import Navigation from '../landing/components/Navigation';
import Footer from '../landing/components/Footer';
import { useSound } from '../landing/hooks';

interface Gateway {
  id: string;
  name: string;
  nameZh: string;
  currencies: string[];
  fee: string;
}

interface CheckoutResponse {
  gateway: string;
  redirectUrl?: string;
  formAction?: string;
  formFields?: Record<string, string>;
  orderRef?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', TWD: 'NT$', EUR: '\u20AC', JPY: '\u00A5', CNY: '\u00A5',
};

function formatPrice(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${amount.toLocaleString()}`;
}

export default function CheckoutPage() {
  const { t, i18n } = useTranslation('landing');
  const { items, getTotal } = useCart();
  const { playNote, soundEnabled, toggleSound } = useSound();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const currency = language === 'zh-TW' ? 'TWD' : language === 'ja' ? 'JPY' : 'USD';

  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSetLanguage = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('i18n_language', lang);
  };

  const navItems = [
    { label: t('nav.services'), href: '/#services', note: 'C5' as const },
    { label: t('nav.products'), href: '/#products', note: 'D5' as const },
    { label: t('nav.portfolio'), href: '/#portfolio', note: 'E5' as const },
    { label: t('nav.industries'), href: '/#industries', note: 'G5' as const },
    { label: t('nav.about'), href: '/#why', note: 'A5' as const },
  ];

  const total = getTotal(currency);
  const lang = language.startsWith('zh') ? 'zh-TW' : language;

  // Fetch available gateways when currency changes
  useEffect(() => {
    get<Gateway[]>(`/api/v1/checkout/gateways?currency=${currency}`)
      .then(data => {
        setGateways(data);
        if (data.length > 0 && !selectedGateway) {
          setSelectedGateway(data[0].id);
        }
      })
      .catch(() => setGateways([]));
  }, [currency]);

  const handleCheckout = async () => {
    if (!email) { setError(t('checkout.emailRequired', 'Email is required')); return; }
    if (!name) { setError(t('checkout.nameRequired', 'Name is required')); return; }
    if (!selectedGateway) { setError(t('checkout.selectGateway', 'Please select a payment method')); return; }

    setSubmitting(true);
    setError('');

    try {
      const resp = await post<CheckoutResponse>('/api/v1/checkout', {
        gateway: selectedGateway,
        currency,
        email,
        name,
        phone,
        items: items.map(i => ({ productId: i.slug, quantity: i.quantity })),
      });

      if (resp.redirectUrl) {
        window.location.href = resp.redirectUrl;
      } else if (resp.formAction && resp.formFields) {
        // ECPay — POST form submission
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = resp.formAction;
        for (const [key, value] of Object.entries(resp.formFields)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err: any) {
      setError(err.message || t('checkout.error', 'Checkout failed. Please try again.'));
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-6">{t('cart.empty', 'Your cart is empty')}</p>
          <Link to="/products/iot" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {t('cart.browseProducts', 'Browse Products')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navigation
        navItems={navItems}
        language={language}
        onSetLanguage={handleSetLanguage}
        soundEnabled={soundEnabled}
        onToggleSound={toggleSound}
        onPlayNote={playNote}
        contactLabel={t('nav.contact')}
        cartItemCount={items.reduce((s, i) => s + i.quantity, 0)}
      />

      <main className="max-w-3xl mx-auto px-6 pt-32 pb-20">
        <h1 className="text-3xl font-bold mb-8">{t('checkout.title', 'Checkout')}</h1>

        {/* Order Summary */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-8">
          <h2 className="text-lg font-semibold mb-4">{t('checkout.orderSummary', 'Order Summary')}</h2>
          <div className="space-y-3">
            {items.map(item => (
              <div key={item.productId} className="flex justify-between text-sm">
                <span className="text-gray-300">
                  {item.name[lang] || item.name['en']} x {item.quantity}
                </span>
                <span className="font-medium">
                  {formatPrice((item.pricing[currency] || 0) * item.quantity, currency)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between">
            <span className="font-semibold">{t('cart.total', 'Total')}</span>
            <span className="text-xl font-bold text-[var(--cyan-electric)]">{formatPrice(total, currency)}</span>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-8">
          <h2 className="text-lg font-semibold mb-4">{t('checkout.contactInfo', 'Contact Information')}</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('checkout.email', 'Email')} *</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('checkout.name', 'Name')} *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('checkout.phone', 'Phone')}</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-8">
          <h2 className="text-lg font-semibold mb-4">{t('checkout.paymentMethod', 'Payment Method')}</h2>
          {gateways.length === 0 ? (
            <p className="text-gray-400 text-sm">
              {t('checkout.noGateways', 'No payment methods available for this currency. Please select TWD.')}
            </p>
          ) : (
            <div className="space-y-3">
              {gateways.map(gw => (
                <label
                  key={gw.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedGateway === gw.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="gateway"
                    value={gw.id}
                    checked={selectedGateway === gw.id}
                    onChange={() => setSelectedGateway(gw.id)}
                    className="text-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium">
                      {lang === 'zh-TW' ? gw.nameZh : gw.name}
                    </div>
                    <div className="text-xs text-gray-400">
                      {t('checkout.fee', 'Fee')}: {gw.fee} | {gw.currencies.join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-4 mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <Link to="/cart" className="text-gray-400 hover:text-white transition-colors">
            {t('checkout.backToCart', 'Back to Cart')}
          </Link>
          <button
            onClick={handleCheckout}
            disabled={submitting || gateways.length === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            {submitting
              ? t('checkout.processing', 'Processing...')
              : t('checkout.payNow', `Pay ${formatPrice(total, currency)}`)}
          </button>
        </div>
      </main>

      <Footer
        companyName={t('footer.companyName')}
        companyNameZh={t('footer.companyNameZh')}
        slogan={t('footer.slogan')}
        copyright={t('footer.copyright')}
        navItems={navItems.map(item => ({ label: item.label, href: item.href }))}
      />
    </div>
  );
}
